import { FontAwesomeIcon, type FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { styled } from '@mui/material';
import { forwardRef } from 'react';

import { resolveColor } from '@@scripts/themes/mui/utils';

import { type BaseProps, type IconSize } from './types';

const StyledFontAwesomeIconWrapper = styled('svg', {
    shouldForwardProp: (prop: string) => prop !== '$color' && prop !== '$size' && prop !== '$stack',
})<{
    $color?: BaseProps['color'];
    $size: IconSize;
    $stack?: boolean;
}>(({ theme, $color, $size, $stack }) => ({
    ...(!$stack && { display: 'inline-block' }),
    fill: 'currentColor',
    overflow: 'visible',
    // @ts-expect-error TS7053 Issue #4120
    height: theme.fixed.icon[$size] || $size,
    ...($color && { color: resolveColor(theme, $color) }),
}));

const StyledFontAwesomeIcon = styled(FontAwesomeIcon)({
    height: 'unset',
});

type Props = Omit<FontAwesomeIconProps, 'color' | 'size'> & {
    color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning' | (string & {});
    size?: IconSize;
};

const BaseIcon = forwardRef<SVGSVGElement, Props>((props, ref) => {
    const {
        color,
        size = 'medium',
        title,
        className,
        icon,
        mask,
        transform,
        'aria-label': ariaLabel,
        ...rest
    } = props;
    // An aria-label can come from outside, e.g. from a wrapping MUI Tooltip
    const label = title ?? ariaLabel;
    const ariaHidden = !label && !rest['aria-hidden'] ? 'true' : 'false';
    const iconClass = className ? `${className} fa-width-auto` : 'fa-width-auto';

    return (
        <StyledFontAwesomeIconWrapper
            {...rest}
            ref={ref}
            // This class is used to prevent font-awesome from setting a custom width
            className={iconClass}
            viewBox="0 0 1 1"
            $color={color}
            $size={size}
            aria-label={label}
            aria-hidden={ariaHidden}
        >
            {title && <title>{title}</title>}
            <StyledFontAwesomeIcon
                icon={icon}
                mask={mask}
                transform={transform}
                aria-hidden={ariaHidden}
            />
        </StyledFontAwesomeIconWrapper>
    );
});

export default BaseIcon;
